import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ArrowUp, ArrowDown, ChevronDown, ChevronRight, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyStage = { name: "", default_days: "", description: "", weight_pct: "" };
const emptySub = { name: "", default_days: "", description: "", weight_pct: "" };

export default function StageMaster() {
  const { user } = useAuth();
  const canEdit = ["ADMIN", "MANAGER"].includes(user?.role);
  const [stages, setStages] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [substages, setSubstages] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyStage);
  const [editing, setEditing] = useState(null);
  const [subOpen, setSubOpen] = useState(null); // stage id currently adding/editing a substage
  const [subForm, setSubForm] = useState(emptySub);
  const [editingSub, setEditingSub] = useState(null);

  // Show all stages (drafts + active) so drafts can be found and activated.
  const load = () => api.get("/stage-master").then((r) => setStages(r.data));
  useEffect(() => { load(); }, []);

  // Running total is informational and reflects only what's actually active.
  const activeStageWeightTotal = stages
    .filter((s) => s.is_active)
    .reduce((sum, s) => sum + (Number(s.weight_pct) || 0), 0);

  const loadSubstages = (stageId) =>
    api.get(`/stage-master/${stageId}/substages`).then((r) =>
      setSubstages((prev) => ({ ...prev, [stageId]: r.data })));

  const toggleExpand = (stageId) => {
    setExpanded((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
    if (!substages[stageId]) loadSubstages(stageId);
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        default_days: form.default_days ? Number(form.default_days) : null,
        description: form.description || null,
        weight_pct: Number(form.weight_pct),
      };
      if (editing) await api.put(`/stage-master/${editing}`, payload);
      else await api.post("/stage-master", payload);
      toast.success("Saved");
      setOpen(false); setForm(emptyStage); setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const activateStage = async (stageId) => {
    try {
      await api.post(`/stage-master/${stageId}/activate`);
      toast.success("Stage activated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteStage = async (stage) => {
    if (!window.confirm(`Permanently delete "${stage.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/stage-master/${stage.id}/purge`);
      toast.success("Stage deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const move = async (idx, dir) => {
    const next = [...stages];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setStages(next);
    try {
      await api.post("/stage-master/reorder", next.map((s) => s.id));
      toast.success("Reordered");
    } catch (e) { toast.error(apiError(e)); load(); }
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ name: s.name, default_days: s.default_days || "", description: s.description || "", weight_pct: s.weight_pct ?? "" });
    setOpen(true);
  };

  const saveSubstage = async (stageId) => {
    try {
      const payload = {
        name: subForm.name,
        default_days: subForm.default_days ? Number(subForm.default_days) : null,
        description: subForm.description || null,
        weight_pct: Number(subForm.weight_pct),
      };
      if (editingSub) await api.put(`/stage-master/${stageId}/substages/${editingSub}`, payload);
      else await api.post(`/stage-master/${stageId}/substages`, payload);
      toast.success("Substage saved");
      setSubOpen(null); setSubForm(emptySub); setEditingSub(null);
      loadSubstages(stageId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const activateSubstage = async (stageId, substageId) => {
    try {
      await api.post(`/stage-master/${stageId}/substages/${substageId}/activate`);
      toast.success("Substage activated");
      loadSubstages(stageId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEditSub = (stageId, s) => {
    setEditingSub(s.id);
    setSubForm({ name: s.name, default_days: s.default_days || "", description: s.description || "", weight_pct: s.weight_pct ?? "" });
    setSubOpen(stageId);
  };

  const moveSub = async (stageId, idx, dir) => {
    const list = substages[stageId] || [];
    const next = [...list];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSubstages((prev) => ({ ...prev, [stageId]: next }));
    try {
      await api.post(`/stage-master/${stageId}/substages/reorder`, next.map((s) => s.id));
    } catch (e) { toast.error(apiError(e)); loadSubstages(stageId); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Stage Master</h1>
          <p className="text-muted-foreground mt-1">The ordered, weighted stages applied to every project.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyStage); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="new-stage-button"><Plus className="h-4 w-4 mr-1" /> New Stage</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Stage</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="stage-name-input" /></div>
                <div className="space-y-1"><Label>Weight (% of overall project)</Label>
                  <Input type="number" value={form.weight_pct} onChange={(e) => setForm({ ...form, weight_pct: e.target.value })} data-testid="stage-weight-input" /></div>
                <div className="space-y-1"><Label>Default Duration (days, per substage default)</Label>
                  <Input type="number" value={form.default_days} onChange={(e) => setForm({ ...form, default_days: e.target.value })} data-testid="stage-days-input" /></div>
                <div className="space-y-1"><Label>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="stage-desc-input" /></div>
              </div>
              <DialogFooter><Button onClick={save} disabled={!form.name || form.weight_pct === ""} data-testid="save-stage-master-button">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="text-sm px-3 py-2 rounded-md bg-muted text-muted-foreground" data-testid="stage-weight-total">
        {activeStageWeightTotal}% of 100% allocated (active)
      </div>

      <div className="space-y-2">
        {stages.map((s, idx) => (
          <Card key={s.id} data-testid={`stage-master-row-${s.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <button onClick={() => toggleExpand(s.id)} data-testid={`toggle-substages-${s.id}`}>
                  {expanded[s.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <span className="text-xs font-mono text-muted-foreground w-6">{s.sequence_no}</span>
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {s.name} <span className="text-xs text-muted-foreground font-normal">({s.weight_pct}%)</span>
                    {s.is_active ? (
                      <Badge variant="secondary" className="gap-1" data-testid={`stage-active-badge-${s.id}`}>
                        <Check className="h-3 w-3" /> Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground" data-testid={`stage-draft-badge-${s.id}`}>
                        Draft
                      </Badge>
                    )}
                  </div>
                  {s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, -1)} data-testid={`stage-up-${s.id}`}><ArrowUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(idx, 1)} data-testid={`stage-down-${s.id}`}><ArrowDown className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEdit(s)} data-testid={`edit-stage-master-${s.id}`}>Edit</Button>
                    {!s.is_active && (
                      <>
                        <Button size="sm" className="rounded-full" onClick={() => activateStage(s.id)} data-testid={`activate-stage-${s.id}`}>Activate</Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => deleteStage(s)} data-testid={`delete-stage-${s.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {expanded[s.id] && (
                <div className="mt-3 ml-9 space-y-2 border-l border-border pl-4">
                  {(() => {
                    const subs = substages[s.id] || [];
                    const activeSubTotal = subs
                      .filter((x) => x.is_active)
                      .reduce((sum, x) => sum + (Number(x.weight_pct) || 0), 0);
                    return (
                      <>
                        <div className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground" data-testid={`substage-weight-total-${s.id}`}>
                          {activeSubTotal}% of 100% allocated within {s.name} (active)
                        </div>
                        {subs.map((sub, sidx) => (
                          <div key={sub.id} className="flex items-center gap-2 text-sm" data-testid={`substage-row-${sub.id}`}>
                            <span className="text-xs font-mono text-muted-foreground w-6">{sub.sequence_no}</span>
                            <span className="flex-1 flex items-center gap-2">
                              {sub.name} <span className="text-xs text-muted-foreground">({sub.weight_pct}%)</span>
                              {sub.is_active ? (
                                <Badge variant="secondary" className="gap-1" data-testid={`substage-active-badge-${sub.id}`}>
                                  <Check className="h-3 w-3" /> Active
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground" data-testid={`substage-draft-badge-${sub.id}`}>
                                  Draft
                                </Badge>
                              )}
                            </span>
                            {canEdit && (
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveSub(s.id, sidx, -1)} data-testid={`substage-up-${sub.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveSub(s.id, sidx, 1)} data-testid={`substage-down-${sub.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                                <Button variant="outline" size="sm" className="rounded-full h-7" onClick={() => openEditSub(s.id, sub)} data-testid={`edit-substage-${sub.id}`}>Edit</Button>
                                {!sub.is_active && (
                                  <Button size="sm" className="rounded-full h-7" onClick={() => activateSubstage(s.id, sub.id)} data-testid={`activate-substage-${sub.id}`}>Activate</Button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    );
                  })()}
                  {canEdit && (
                    <Button variant="ghost" size="sm" className="rounded-full"
                      onClick={() => { setEditingSub(null); setSubForm(emptySub); setSubOpen(s.id); }}
                      data-testid={`add-substage-${s.id}`}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Substage
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!subOpen} onOpenChange={(o) => { if (!o) { setSubOpen(null); setSubForm(emptySub); setEditingSub(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingSub ? "Edit" : "New"} Substage</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name</Label>
              <Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} data-testid="substage-name-input" /></div>
            <div className="space-y-1"><Label>Weight (% within this stage)</Label>
              <Input type="number" value={subForm.weight_pct} onChange={(e) => setSubForm({ ...subForm, weight_pct: e.target.value })} data-testid="substage-weight-input" /></div>
            <div className="space-y-1"><Label>Default Duration (days)</Label>
              <Input type="number" value={subForm.default_days} onChange={(e) => setSubForm({ ...subForm, default_days: e.target.value })} data-testid="substage-days-input" /></div>
            <div className="space-y-1"><Label>Description</Label>
              <Input value={subForm.description} onChange={(e) => setSubForm({ ...subForm, description: e.target.value })} data-testid="substage-desc-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={() => saveSubstage(subOpen)} disabled={!subForm.name || subForm.weight_pct === ""} data-testid="save-substage-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
